// Agency Dashboard - Command center for agency owners
import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Users, AlertTriangle, TrendingUp, TrendingDown,
  Clock, ChevronRight, Phone, Mail, Building2, Shield,
  UserCheck, UserX, DollarSign, CheckCircle2, XCircle,
  RefreshCw, Activity, Bell, Target, BarChart3
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { csrfHeadersFor } from '../api';

const BASE = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

// Risk level colors matching existing system
const RISK_COLORS = {
  severe: { bg: 'bg-purple-800', text: 'text-white', light: 'bg-purple-100 text-purple-900' },
  critical: { bg: 'bg-red-600', text: 'text-white', light: 'bg-red-100 text-red-800' },
  high: { bg: 'bg-orange-500', text: 'text-white', light: 'bg-orange-100 text-orange-800' },
  elevated: { bg: 'bg-yellow-500', text: 'text-white', light: 'bg-yellow-100 text-yellow-800' },
  moderate: { bg: 'bg-blue-500', text: 'text-white', light: 'bg-blue-100 text-blue-800' },
  low: { bg: 'bg-green-400', text: 'text-white', light: 'bg-green-100 text-green-800' },
  stable: { bg: 'bg-green-600', text: 'text-white', light: 'bg-green-100 text-green-800' }
};

// Metric Card Component
function MetricCard({ title, value, subtitle, icon: Icon, color, trend, onClick, loading }) {
  return (
    <div
      onClick={onClick}
      className={`bg-white rounded-2xl shadow p-4 sm:p-6 border-t-4 transition-all duration-200 ${
        onClick ? 'cursor-pointer hover:shadow-lg hover:scale-[1.02]' : ''
      }`}
      style={{ borderTopColor: color || '#FFB800' }}
    >
      {loading ? (
        <div className="animate-pulse space-y-3">
          <div className="h-4 bg-gray-200 rounded w-20"></div>
          <div className="h-8 bg-gray-200 rounded w-16"></div>
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-500 font-medium">{title}</span>
            {Icon && <Icon size={20} className="text-gray-400" />}
          </div>
          <div className="flex items-end gap-2">
            <span className="text-2xl sm:text-3xl font-bold text-[#172A3A]">{value}</span>
            {trend && (
              <span className={`text-xs font-medium flex items-center ${
                trend > 0 ? 'text-green-600' : trend < 0 ? 'text-red-600' : 'text-gray-400'
              }`}>
                {trend > 0 ? <TrendingUp size={14} /> : trend < 0 ? <TrendingDown size={14} /> : null}
                {trend !== 0 && ` ${Math.abs(trend)}%`}
              </span>
            )}
          </div>
          {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
        </>
      )}
    </div>
  );
}

// Risk Breakdown Widget - Shows risk by level with click-through
function RiskBreakdownWidget({ data, loading, onLevelClick }) {
  const levels = [
    { key: 'severe', label: 'Severe', color: RISK_COLORS.severe },
    { key: 'critical', label: 'Critical', color: RISK_COLORS.critical },
    { key: 'high', label: 'High', color: RISK_COLORS.high },
    { key: 'elevated', label: 'Elevated', color: RISK_COLORS.elevated },
    { key: 'moderate', label: 'Moderate', color: RISK_COLORS.moderate },
    { key: 'low', label: 'Low', color: RISK_COLORS.low },
    { key: 'stable', label: 'Stable', color: RISK_COLORS.stable }
  ];

  const total = levels.reduce((sum, l) => sum + (data?.[l.key] || 0), 0);
  const atRisk = (data?.severe || 0) + (data?.critical || 0) + (data?.high || 0) + (data?.elevated || 0);

  return (
    <div className="bg-white rounded-2xl shadow p-6 border-t-4" style={{ borderTopColor: '#dc2626' }}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-[#172A3A]">Risk Score Breakdown</h3>
        <Link to="/agency/risk" className="text-sm text-blue-600 hover:underline flex items-center gap-1">
          View All <ChevronRight size={16} />
        </Link>
      </div>

      {loading ? (
        <div className="animate-pulse space-y-3">
          {[1, 2, 3, 4, 5, 6].map(i => (
            <div key={i} className="h-8 bg-gray-100 rounded"></div>
          ))}
        </div>
      ) : (
        <>
          {/* Summary */}
          <div className="mb-4 p-3 bg-red-50 rounded-xl border border-red-200">
            <div className="flex items-center gap-2">
              <AlertTriangle size={18} className="text-red-600" />
              <span className="font-semibold text-red-800">{atRisk} clients at risk</span>
            </div>
            <p className="text-xs text-red-600 mt-1">Require immediate attention</p>
          </div>

          {/* Breakdown bars */}
          <div className="space-y-2">
            {levels.map(({ key, label, color }) => {
              const count = data?.[key] || 0;
              const pct = total > 0 ? (count / total) * 100 : 0;
              return (
                <button
                  key={key}
                  onClick={() => onLevelClick?.(key)}
                  className="w-full group"
                >
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="text-gray-700 group-hover:text-[#172A3A] font-medium">{label}</span>
                    <span className="font-semibold text-gray-900">{count}</span>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full ${color.bg} transition-all duration-300 group-hover:opacity-80`}
                      style={{ width: `${Math.max(pct, count > 0 ? 2 : 0)}%` }}
                    />
                  </div>
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

// Agent Performance Widget
function AgentPerformanceWidget({ agents, loading }) {
  const navigate = useNavigate();

  return (
    <div className="bg-white rounded-2xl shadow p-6 border-t-4" style={{ borderTopColor: '#172A3A' }}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-[#172A3A]">Agent Performance</h3>
        <Link to="/agency/agents" className="text-sm text-blue-600 hover:underline flex items-center gap-1">
          View All <ChevronRight size={16} />
        </Link>
      </div>

      {loading ? (
        <div className="animate-pulse space-y-3">
          {[1, 2, 3, 4, 5].map(i => (
            <div key={i} className="h-12 bg-gray-100 rounded-xl"></div>
          ))}
        </div>
      ) : agents?.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <Users size={32} className="mx-auto mb-2 text-gray-300" />
          <p className="text-sm">No agents in your agency yet</p>
          <Link to="/agency/agents" className="text-blue-600 text-sm hover:underline mt-2 inline-block">
            Add agents
          </Link>
        </div>
      ) : (
        <div className="space-y-2">
          {agents?.slice(0, 5).map((agent, idx) => (
            <button
              key={agent.agentId}
              onClick={() => navigate(`/agency/agents/${agent.agentId}`)}
              className="w-full flex items-center gap-3 p-3 hover:bg-gray-50 rounded-xl transition-colors text-left"
            >
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                idx === 0 ? 'bg-yellow-100 text-yellow-700' :
                idx === 1 ? 'bg-gray-200 text-gray-600' :
                idx === 2 ? 'bg-orange-100 text-orange-700' :
                'bg-slate-100 text-slate-600'
              }`}>
                {idx + 1}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-gray-900 truncate">{agent.agentName}</div>
                <div className="text-xs text-gray-500">{agent.totalClients} clients</div>
              </div>
              <div className="text-right">
                <div className="text-sm font-semibold text-gray-900">
                  {agent.riskBreakdown?.critical + agent.riskBreakdown?.high || 0}
                </div>
                <div className="text-xs text-red-600">at risk</div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Activity Feed Widget
function ActivityFeedWidget({ activities, loading }) {
  const navigate = useNavigate();

  const getActivityIcon = (action) => {
    switch (action) {
      case 'CLIENT_CREATE': return <UserCheck size={16} className="text-green-600" />;
      case 'CLIENT_CHURN': return <UserX size={16} className="text-red-600" />;
      case 'CALL_LOG': return <Phone size={16} className="text-blue-600" />;
      case 'SMS_SENT': return <Mail size={16} className="text-purple-600" />;
      case 'EMAIL_SENT': return <Mail size={16} className="text-indigo-600" />;
      case 'TASK_COMPLETE': return <CheckCircle2 size={16} className="text-green-600" />;
      case 'RISK_ALERT': return <AlertTriangle size={16} className="text-orange-600" />;
      default: return <Activity size={16} className="text-gray-400" />;
    }
  };

  const formatTime = (date) => {
    if (!date) return '';
    const d = new Date(date);
    const now = new Date();
    const diff = (now - d) / 1000;
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return d.toLocaleDateString();
  };

  return (
    <div className="bg-white rounded-2xl shadow p-6 border-t-4" style={{ borderTopColor: '#007cf0' }}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-[#172A3A]">Recent Activity</h3>
        <Activity size={20} className="text-gray-400" />
      </div>

      {loading ? (
        <div className="animate-pulse space-y-3">
          {[1, 2, 3, 4, 5].map(i => (
            <div key={i} className="h-12 bg-gray-100 rounded-xl"></div>
          ))}
        </div>
      ) : activities?.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <Activity size={32} className="mx-auto mb-2 text-gray-300" />
          <p className="text-sm">No recent activity</p>
        </div>
      ) : (
        <div className="space-y-2">
          {activities?.slice(0, 8).map((activity) => (
            <div
              key={activity.id}
              className="flex items-start gap-3 p-2 hover:bg-gray-50 rounded-lg transition-colors"
            >
              <div className="mt-0.5">{getActivityIcon(activity.action)}</div>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-gray-900">
                  <span className="font-medium">{activity.user?.name || 'Unknown'}</span>
                  {' '}
                  <span className="text-gray-600">
                    {activity.action?.replace(/_/g, ' ').toLowerCase()}
                  </span>
                  {activity.client && (
                    <>
                      {' - '}
                      <button
                        onClick={() => navigate(`/clients/${activity.client.id}`)}
                        className="text-blue-600 hover:underline"
                      >
                        {activity.client.name}
                      </button>
                    </>
                  )}
                </div>
                <div className="text-xs text-gray-500">{formatTime(activity.createdAt)}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Alerts Widget
function AlertsWidget({ alerts, unreadCount, loading, onMarkRead }) {
  const navigate = useNavigate();

  const getSeverityStyles = (severity) => {
    switch (severity) {
      case 'critical': return 'bg-red-50 border-red-200 text-red-800';
      case 'warning': return 'bg-yellow-50 border-yellow-200 text-yellow-800';
      default: return 'bg-blue-50 border-blue-200 text-blue-800';
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow p-6 border-t-4" style={{ borderTopColor: '#f97316' }}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <h3 className="text-lg font-semibold text-[#172A3A]">Alerts</h3>
          {unreadCount > 0 && (
            <span className="px-2 py-0.5 bg-red-500 text-white text-xs font-bold rounded-full">
              {unreadCount}
            </span>
          )}
        </div>
        <Link to="/agency/alerts" className="text-sm text-blue-600 hover:underline flex items-center gap-1">
          View All <ChevronRight size={16} />
        </Link>
      </div>

      {loading ? (
        <div className="animate-pulse space-y-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-16 bg-gray-100 rounded-xl"></div>
          ))}
        </div>
      ) : alerts?.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <Bell size={32} className="mx-auto mb-2 text-gray-300" />
          <p className="text-sm">No new alerts</p>
        </div>
      ) : (
        <div className="space-y-2">
          {alerts?.slice(0, 5).map((alert) => (
            <div
              key={alert.id}
              className={`p-3 rounded-xl border ${getSeverityStyles(alert.severity)} ${
                !alert.isRead ? 'ring-2 ring-offset-1 ring-yellow-400' : ''
              }`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1">
                  <div className="font-medium text-sm">{alert.title}</div>
                  {alert.message && (
                    <div className="text-xs opacity-80 mt-0.5">{alert.message}</div>
                  )}
                </div>
                {!alert.isRead && (
                  <button
                    onClick={() => onMarkRead?.(alert.id)}
                    className="text-xs text-gray-500 hover:text-gray-700"
                  >
                    Dismiss
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Quick Stats Row
function QuickStatsRow({ metrics, loading }) {
  const stats = [
    {
      label: 'Tasks Overdue',
      value: metrics?.tasksOverdue || 0,
      icon: Clock,
      color: metrics?.tasksOverdue > 0 ? '#dc2626' : '#22c55e',
      alert: metrics?.tasksOverdue > 0
    },
    {
      label: 'Churned (12mo)',
      value: metrics?.churnedLast30Days || 0,
      icon: UserX,
      color: '#ef4444'
    },
    {
      label: 'New Clients (30d)',
      value: metrics?.newClientsLast30Days || 0,
      icon: UserCheck,
      color: '#22c55e'
    },
    {
      label: 'Revenue at Risk',
      value: `$${(metrics?.revenueAtRisk || 0).toLocaleString()}`,
      icon: DollarSign,
      color: '#f97316'
    }
  ];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      {stats.map((stat, idx) => (
        <div
          key={idx}
          className={`bg-white rounded-xl shadow-sm p-4 flex items-center gap-3 ${
            stat.alert ? 'ring-2 ring-red-200' : ''
          }`}
        >
          {loading ? (
            <div className="animate-pulse flex items-center gap-3 w-full">
              <div className="w-10 h-10 bg-gray-200 rounded-lg"></div>
              <div className="flex-1">
                <div className="h-3 bg-gray-200 rounded w-16 mb-2"></div>
                <div className="h-5 bg-gray-200 rounded w-10"></div>
              </div>
            </div>
          ) : (
            <>
              <div
                className="w-10 h-10 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: `${stat.color}15` }}
              >
                <stat.icon size={20} style={{ color: stat.color }} />
              </div>
              <div>
                <div className="text-xs text-gray-500">{stat.label}</div>
                <div className="text-lg font-bold text-gray-900">{stat.value}</div>
              </div>
            </>
          )}
        </div>
      ))}
    </div>
  );
}

// Main Agency Dashboard Component
export default function AgencyDashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState(null);
  const [riskSummary, setRiskSummary] = useState(null);
  const [activities, setActivities] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [unreadAlertCount, setUnreadAlertCount] = useState(0);
  const [error, setError] = useState(null);
  const [agency, setAgency] = useState(null);

  // Fetch all dashboard data
  useEffect(() => {
    async function loadDashboard() {
      setLoading(true);
      setError(null);

      try {
        const [dashboardRes, riskRes, activityRes, alertsRes] = await Promise.all([
          fetch(`${BASE}/agency/dashboard`, { credentials: 'include' }),
          fetch(`${BASE}/agency/risk-summary`, { credentials: 'include' }),
          fetch(`${BASE}/agency/activity-feed?limit=10`, { credentials: 'include' }),
          fetch(`${BASE}/agency/alerts?unreadOnly=true&limit=10`, { credentials: 'include' })
        ]);

        // Check if user has access
        if (dashboardRes.status === 403) {
          navigate('/dashboard');
          return;
        }

        if (dashboardRes.status === 404) {
          // No agency setup yet - redirect to setup
          navigate('/agency/setup');
          return;
        }

        const [dashboardData, riskData, activityData, alertsData] = await Promise.all([
          dashboardRes.json().catch(() => ({})),
          riskRes.json().catch(() => ({})),
          activityRes.json().catch(() => ({})),
          alertsRes.json().catch(() => ({}))
        ]);

        if (dashboardData.ok) {
          setMetrics(dashboardData.metrics);
          setAgency(dashboardData.agency);
        }

        if (riskData.ok) {
          setRiskSummary(riskData);
        }

        if (activityData.ok) {
          setActivities(activityData.activities);
        }

        if (alertsData.ok) {
          setAlerts(alertsData.alerts);
          setUnreadAlertCount(alertsData.unreadCount);
        }

      } catch (err) {
        console.error('[AgencyDashboard] Error loading data:', err);
        setError('Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    }

    loadDashboard();
  }, [navigate]);

  // Handle risk level click
  const handleRiskLevelClick = (level) => {
    navigate(`/agency/risk?level=${level}`);
  };

  // Handle alert mark as read
  const handleMarkAlertRead = async (alertId) => {
    try {
      const res = await fetch(`${BASE}/agency/alerts/${alertId}/read`, {
        method: 'POST',
        credentials: 'include',
        headers: csrfHeadersFor('POST')
      });
      if (!res.ok) return;
      // Remove dismissed alert from list and decrement badge — then re-fetch to sync server state
      setAlerts(prev => prev.filter(a => a.id !== alertId));
      setUnreadAlertCount(prev => Math.max(0, prev - 1));
      // Re-fetch to ensure list is in sync with server
      const refreshed = await fetch(`${BASE}/agency/alerts?unreadOnly=true&limit=10`, { credentials: 'include' });
      if (refreshed.ok) {
        const data = await refreshed.json();
        if (data.ok) {
          setAlerts(data.alerts);
          setUnreadAlertCount(data.unreadCount);
        }
      }
    } catch (err) {
      console.error('Failed to mark alert as read:', err);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 text-center max-w-md">
          <XCircle size={48} className="mx-auto text-red-500 mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Error Loading Dashboard</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-[#172A3A] text-white rounded-lg hover:opacity-90"
          >
            <RefreshCw size={16} className="inline mr-2" />
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 py-4 sm:py-6 lg:py-8 px-3 sm:px-4 lg:px-6">
      <div className="max-w-7xl mx-auto space-y-4 sm:space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <Building2 size={28} className="text-[#172A3A]" />
              <h1 className="text-2xl sm:text-3xl font-bold text-[#172A3A]">
                Agency Dashboard
              </h1>
            </div>
            {agency && (
              <p className="text-gray-600 mt-1 ml-10">{agency.name}</p>
            )}
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => window.location.reload()}
              className="p-2 hover:bg-white rounded-lg transition-colors"
              title="Refresh"
            >
              <RefreshCw size={20} className="text-gray-500" />
            </button>
            <Link
              to="/agency/agents"
              className="px-4 py-2 bg-[#172A3A] text-white rounded-lg hover:opacity-90 flex items-center gap-2"
            >
              <Users size={18} />
              <span className="hidden sm:inline">Manage Agents</span>
            </Link>
          </div>
        </div>

        {/* Top Metric Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <MetricCard
            title="Total Clients"
            value={metrics?.totalClients?.toLocaleString() || '0'}
            subtitle={`${metrics?.activeClients || 0} active`}
            icon={Users}
            color="#172A3A"
            loading={loading}
            onClick={() => navigate('/agency/clients')}
          />
          <MetricCard
            title="Total Agents"
            value={metrics?.totalAgents || '0'}
            subtitle="In your agency"
            icon={UserCheck}
            color="#FFB800"
            loading={loading}
            onClick={() => navigate('/agency/agents')}
          />
          <MetricCard
            title="Clients at Risk"
            value={(metrics?.riskBreakdown?.severe || 0) + (metrics?.riskBreakdown?.critical || 0) + (metrics?.riskBreakdown?.high || 0)}
            subtitle="Severe + Critical + High"
            icon={AlertTriangle}
            color="#dc2626"
            loading={loading}
            onClick={() => navigate('/agency/risk?level=high')}
          />
          <MetricCard
            title="Retention Rate"
            value={`${metrics?.retentionRate || 0}%`}
            subtitle="Last 12 months"
            icon={TrendingUp}
            color="#22c55e"
            loading={loading}
          />
        </div>

        {/* Quick Stats Row */}
        <QuickStatsRow metrics={metrics} loading={loading} />

        {/* Main Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
          {/* Left Column - Risk & Performance */}
          <div className="lg:col-span-2 space-y-4 sm:space-y-6">
            {/* Risk Breakdown */}
            <RiskBreakdownWidget
              data={metrics?.riskBreakdown}
              loading={loading}
              onLevelClick={handleRiskLevelClick}
            />

            {/* Activity Feed */}
            <ActivityFeedWidget
              activities={activities}
              loading={loading}
            />
          </div>

          {/* Right Column - Agents & Alerts */}
          <div className="space-y-4 sm:space-y-6">
            {/* Agent Performance */}
            <AgentPerformanceWidget
              agents={riskSummary?.agents}
              loading={loading}
            />

            {/* Alerts */}
            <AlertsWidget
              alerts={alerts}
              unreadCount={unreadAlertCount}
              loading={loading}
              onMarkRead={handleMarkAlertRead}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
